import './IncrementalRoas.scss';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import { MdArrowForward } from 'react-icons/md';
import {
  IncrementalRoasTakeaways,
  WeeklyIncrementalRoas
} from '@blisspointmedia/bpm-types/dist/BrandEquity';
import { DateGroupingKey } from '@blisspointmedia/bpm-types/dist/FiscalCalendar';
import { ChartContainer } from '@Components/ChartContainer/ChartContainer';
import { ComposedChart } from '@Components/Charts/ComposedChart/ComposedChart';
import { Placement, Size } from '@Components/designConstants';
import { DownloadDropdown } from '@Components/DownloadDropdown/DownloadDropdown';
import { Dropdown, DropdownToggleType } from '@Components/Dropdown';
import { WidgetContainer } from '@Components/WidgetContainer/WidgetContainer';
import { Brand60 } from '@src/utils/colors';
import { dateToMddyy } from '@src/utils/date-utils';
import { downloadPNG, exportToExcel } from '@src/utils/download-utils';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { DATE_GROUPING_OPTIONS_BRAND_EQUITY, groupChartDataByDate } from '../BrandEquityUtils';
import { currFormat } from '../BrandImpact/BrandImpactUtils';

interface IncrementalRoasProps {
  weeklyIncrementalRoas: WeeklyIncrementalRoas[];
  incrementalRoasTakeaways: IncrementalRoasTakeaways;
}

export const IncrementalRoas = ({
  weeklyIncrementalRoas,
  incrementalRoasTakeaways
}: IncrementalRoasProps): ReactElement => {
  const [weeklyIncrementalRoasChartDateGrouping, setWeeklyIncrementalRoasChartDateGrouping] =
    useState('Week');
  const { fiscal_cal: fiscalCal } = useCompanyInfo();

  const { res: weeklyIncrementalRoasByDate } = useMemo(() => {
    if (weeklyIncrementalRoas.length > 0) {
      const groupByDateGqvData = groupChartDataByDate(
        weeklyIncrementalRoas.map(line => ({
          ...line,
          Revenue: line.IncrementalRoas * line.Spend
        })),
        weeklyIncrementalRoasChartDateGrouping as DateGroupingKey,
        fiscalCal
      );
      return {
        res: groupByDateGqvData.map(line => ({
          ...line,
          IncrementalRoas: line.Revenue / line.Spend
        }))
      };
    }
    return { res: [] };
  }, [weeklyIncrementalRoasChartDateGrouping, weeklyIncrementalRoas, fiscalCal]);

  const excelDownloadWeeklyIncrementalRoas = useCallback(() => {
    exportToExcel(weeklyIncrementalRoas, 'weekly_incremental_roas');
  }, [weeklyIncrementalRoas]);

  const pngDownloadWeeklyIncrementalRoas = useCallback(async () => {
    await downloadPNG('.incremental-roas .left-left', 'weekly_incremental_roas');
  }, []);

  return (
    <WidgetContainer
      collapsible
      header='What is our incremental ROAS from media-driven brand equity?'
      subHeader="Brand media investments' contribution to the bottom line."
    >
      <div className='incremental-roas'>
        <div className='left-left'>
          {weeklyIncrementalRoas.length !== 0 && (
            <ChartContainer
              rightActions={
                <div className='incremental-roas-right-actions'>
                  <DownloadDropdown
                    onClickOptions={[
                      excelDownloadWeeklyIncrementalRoas,
                      pngDownloadWeeklyIncrementalRoas
                    ]}
                  />
                </div>
              }
              title={
                <>
                  <Dropdown
                    overlayPlacement={Placement.RIGHT}
                    size={Size.LG}
                    type={DropdownToggleType.WIDGET_TITLE}
                    value={weeklyIncrementalRoasChartDateGrouping}
                    options={DATE_GROUPING_OPTIONS_BRAND_EQUITY}
                    onChange={option => setWeeklyIncrementalRoasChartDateGrouping(option)}
                  />
                  <div>
                    Incremental ROAS
                    <span className='title-subtext'>
                      {' (Revenue From Media-Driven Brand Equity / Spend)'}
                    </span>
                  </div>
                </>
              }
              titleAfterDashText={`${dateToMddyy(weeklyIncrementalRoas[0].date)} – ${dateToMddyy(
                weeklyIncrementalRoas[weeklyIncrementalRoas.length - 1].date
              )}`}
              beforeTooltipText='Incremental ROAS'
            >
              <ComposedChart
                data={weeklyIncrementalRoasByDate}
                xAxisDataKey='date'
                xAxisTickFormatter={val => dateToMddyy(val)}
                dateGrouping={weeklyIncrementalRoasChartDateGrouping}
                areaYAxisTickFormatter={value => currFormat(value, 0)}
                colorOverrides={{
                  'Revenue From Media-Driven Brand Equity': Brand60,
                  // No palette equivalent; matches prod exactly.
                  IncrementalRoas: '#79DFF5'
                }}
                areas={[
                  {
                    name: 'Incremental ROAS',
                    dataKey: 'IncrementalRoas',
                    line: true
                  }
                ]}
                tooltipFormatter={val => {
                  if (!val) {
                    return val;
                  }
                  if (val <= 100) {
                    return `${val.toFixed(2)}`;
                  }
                  return currFormat(val, 2);
                }}
                reverseToolTipItems
                reverseLegend
                areaYAxisOrientation='right'
              />
            </ChartContainer>
          )}
        </div>
        <div className='left-right'>
          <ChartContainer title='Takeaways' titleAfterDashText='TTM'>
            <div className='parent-incremental-takeaways'>
              <div className='top-incremental'>
                <div className='top-text-box'>
                  <div />
                  <div className='top-text'>Spend </div>
                  <div className='top-text'>
                    {`$${(incrementalRoasTakeaways.paidMediaSpend / 1000000).toLocaleString(
                      'en-US',
                      {
                        maximumFractionDigits: 2
                      }
                    )}M`}
                  </div>
                </div>
                <div className='icon-holder'>
                  <MdArrowForward className='icon' />
                </div>
                <div className='top-text-box'>
                  <div />
                  <div className='top-text'>Brand</div>
                  <div className='top-text'>Equity</div>
                </div>
                <div className='icon-holder'>
                  <MdArrowForward className='icon' />
                </div>
                <div className='top-text-box'>
                  <div>Incremental</div>
                  <div className='top-text'>Revenue</div>
                  <div className='top-text'>{`~$${(
                    incrementalRoasTakeaways.spendInducedBrandRevenue / 1000000
                  ).toLocaleString('en-US', {
                    maximumFractionDigits: 2
                  })}M`}</div>
                </div>
              </div>
              <div className='middle-incremental'>
                <div className='middle-text'>{`Incremental ROAS = ${
                  Number.isFinite(incrementalRoasTakeaways.iRoasTtm)
                    ? incrementalRoasTakeaways.iRoasTtm.toLocaleString('en-US', {
                        maximumFractionDigits: 2
                      })
                    : incrementalRoasTakeaways.iRoasTtm
                }`}</div>
              </div>
              <div className='bottom-incremental'>
                <div className='bottom-text'>
                  {`We estimate paid media-driven brand equity led to an incremental ROAS of ${
                    Number.isFinite(incrementalRoasTakeaways.iRoasTtm)
                      ? incrementalRoasTakeaways.iRoasTtm.toLocaleString('en-US', {
                          maximumFractionDigits: 2
                        })
                      : incrementalRoasTakeaways.iRoasTtm
                  } over  the trailing twelve months.`}
                </div>
              </div>
            </div>
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};
